import { useState, useEffect } from 'react'
import { Eye, Lock, BadgeDollarSign, MousePointerClick, Percent, UserRound, Search, Bell, Settings, Target, Calendar} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { Bar, BarChart, XAxis, YAxis, CartesianGrid, ResponsiveContainer } from "recharts"
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Footer } from "@/components/ui/footer"
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover"
import { Calendar as CalendarComponent } from "@/components/ui/calendar"
import { format, addDays } from "date-fns";
import { ptBR } from "date-fns/locale"
import PasswordChange from './lib/PasswordChange.tsx'
import Banner from './components/Banner'
import Cookie from './components/ui/cookie'
import Newsletters from './lib/Newsletters/NewslettersCards'
import { ChartContainer, ChartTooltip, ChartTooltipContent } from "@/components/ui/chart"
import DailyMetricsChart from './lib/AllMetricsChart.tsx'
import UploadAnalysis from './lib/csvUpload.tsx'
import Changelog from './lib/Updates.tsx'
import LoadingSpinner from './components/LoadingComponent'
import NewsletterInsights from './lib/Newsletters/NewsletterAnalysis'
import { ThemeProvider, ThemeToggle } from "@/lib/ThemeProvider"
import ChatBubble from './components/BubbleChat.tsx'
import { CampaignsView } from './components/Campanhas/Campaigns.tsx'
import { FullStackCandidatesDashboard } from './lib/Hiring.tsx'
import StreaksDashboard from './components/Streaks/Dashboard.tsx'
import { getDateRange } from './lib/utils/date'
import LoginForm from './lib/utils/LoginForm.tsx'
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { toast } from 'sonner'
import URLShortener from './pages/Vendas/URLShortener.tsx';

interface User {
  email: string
  isAuthenticated: boolean
  token: string
}

interface PlatformMetrics {
  spend: number
  conversions: number
  clicks: number
  impressions: number
}

interface Metrics {
  [platform: string]: PlatformMetrics
}

const MetricsLoadingSpinner = () => (
  <div className="flex items-center justify-center h-16">
    <div className="w-5 h-5 border-2 border-t-[#FF3366] rounded-full animate-spin" />
  </div>
)

export default function Component() {
  const [user, setUser] = useState<User | null>(() => {
    if (typeof window !== 'undefined') {
      const storedUser = localStorage.getItem('user')
      if (storedUser) {
        try {
          return JSON.parse(storedUser)
        } catch {
          return null
        }
      }
    }
    return null
  })
  const [pageLoading, _setPageLoading] = useState(false)
  const [metricsLoading, setMetricsLoading] = useState(false)
  const [email, setEmail] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)
  const [selectedPeriod, setSelectedPeriod] = useState('24h')
  const [isCalendarOpen, setIsCalendarOpen] = useState(false)
  const [dateRange, setDateRange] = useState({
    from: undefined,
    to: undefined,
  })
  const [isCustomDate, setIsCustomDate] = useState(false)
  const [metrics, setMetrics] = useState<Metrics>({})
  const [showPasswordChange, setShowPasswordChange] = useState(false)
  const [googleError, setGoogleError] = useState(false);

//   const authenticateUser = async (email: string, password: string) => {
//     try {
//       const response = await fetch('https://waffle-v2.thenewcc.workers.dev/auth/login', {
//         method: 'POST',
//         headers: {
//           'Content-Type': 'application/json'
//         },
//         body: JSON.stringify({ email, password })
//       })

//       if (!response.ok) {
//         const errorData = await response.json()
//         throw new Error(errorData.message || 'Credenciais inválidas')
//       }

//       const data = await response.json()
//       return data.token
//     } catch (error) {
//       console.error('Error during authentication:', error)
//       throw error
//     }
//   }

  // DateRangePicker component atualizado
  const DateRangePicker = () => {
    const handleSelect = (range: any) => {
      setDateRange(range)
      // Só fecha o calendário quando ambas as datas estão selecionadas
      if (range.from && range.to) {
        setIsCalendarOpen(false)
      }
    }


    return (
      <div className="flex items-center gap-2">
        <Select
          value={selectedPeriod}
          onValueChange={(value) => {
            setSelectedPeriod(value)
            setIsCustomDate(value === 'custom')
            if (value !== 'custom') {
              setDateRange({ from: undefined, to: undefined })
            }
          }}
        >
          <SelectTrigger className="w-[160px]">
            <SelectValue placeholder="Selecione o período" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="24h">Hoje</SelectItem>
            <SelectItem value="Ontem">Ontem</SelectItem>
            <SelectItem value="7d">Últimos 7 dias</SelectItem>
            <SelectItem value="14d">Últimos 14 dias</SelectItem>
            <SelectItem value="30d">Últimos 30 dias</SelectItem>
            <SelectItem value="thisWeek">Esta semana</SelectItem>
            <SelectItem value="lastWeek">Semana passada</SelectItem>
            <SelectItem value="thisMonth">Este mês</SelectItem>
            <SelectItem value="lastMonth">Mês passado</SelectItem>
            <SelectItem value="maximum">Máximo (365 dias)</SelectItem>
            <SelectItem value="custom">Personalizado</SelectItem>
          </SelectContent>
        </Select>

        {isCustomDate && (
          <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
            <PopoverTrigger asChild>
              <Button
                variant="outline"
                className={`w-[280px] justify-start text-left font-normal ${!dateRange.from && "text-muted-foreground"
                  }`}
                onClick={() => setIsCalendarOpen(true)}
              >
                <Calendar className="mr-2 h-4 w-4" />
                {dateRange.from ? (
                  dateRange.to ? (
                    <>
                      {format(dateRange.from, "dd/MM/yyyy", { locale: ptBR })} -{" "}
                      {format(dateRange.to, "dd/MM/yyyy", { locale: ptBR })}
                    </>
                  ) : (
                    format(dateRange.from, "dd/MM/yyyy", { locale: ptBR })
                  )
                ) : (
                  <span>Selecione um período</span>
                )}
              </Button>
            </PopoverTrigger>
            <PopoverContent className="w-auto p-0 bg-[#FFFFFF]" align="start">
              <CalendarComponent
                initialFocus
                mode="range"
                defaultMonth={dateRange.from}
                selected={dateRange}
                onSelect={handleSelect}
                numberOfMonths={2}
                locale={ptBR}
                disabled={(date) => date > new Date() || date < addDays(new Date(), -365)}
                fixedWeeks
              />
            </PopoverContent>
          </Popover>
        )}
      </div>
    )
  }
  // Update the fetchMetrics function with the correct endpoint structure
  const fetchMetrics = async () => {
    if (!user?.token) return;

    setMetricsLoading(true);
    setGoogleError(false);
    try {
      let startDate, endDate;

      if (isCustomDate && dateRange.from && dateRange.to) {
        startDate = format(dateRange.from, 'yyyy-MM-dd');
        endDate = format(dateRange.to, 'yyyy-MM-dd');
      } else {
        const dates = getDateRange(selectedPeriod);
        if (!dates) return;
        startDate = dates.startDate;
        endDate = dates.endDate;
      }

      const newMetrics: Metrics = {};

      // Buscar dados do Facebook e TikTok primeiro
      for (const platform of ['facebook', 'tiktok']) {
        try {
          const response = await fetch(
            `https://waffle-v2.thenewcc.workers.dev/metrics/${platform}/${startDate}/${endDate}`,
            {
              headers: {
                'Authorization': `Bearer ${user.token}`
              }
            }
          );

          if (response.status === 401 || response.status === 403) {
            localStorage.removeItem('user');
            window.location.reload();
            throw new Error('Sessão expirada. Por favor, faça login novamente.');
          }
          
          if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
          const result = await response.json();
          
          newMetrics[platform] = {
            spend: result?.spend || 0,
            conversions: result?.conversions || 0,
            clicks: result?.clicks || 0,
            impressions: result?.impressions || 0
          };
        } catch (error) {
          console.error(`Erro ao carregar dados do ${platform}:`, error);
          newMetrics[platform] = {
            spend: 0,
            conversions: 0,
            clicks: 0,
            impressions: 0
          };
        }
      }

      // Tentar buscar dados do Google separadamente
      try {
        const googleResponse = await fetch(
          `https://waffle-v2.thenewcc.workers.dev/metrics/google/${startDate}/${endDate}`,
          {
            headers: {
              'Authorization': `Bearer ${user.token}`
            }
          }
        );

        if (googleResponse.status === 401 || googleResponse.status === 403) {
          localStorage.removeItem('user');
          window.location.reload();
          throw new Error('Sessão expirada. Por favor, faça login novamente.');
        }
        
        if (!googleResponse.ok) throw new Error(`HTTP error! status: ${googleResponse.status}`);
        const googleResult = await googleResponse.json();
        
        newMetrics.google = {
          spend: googleResult?.spend || 0,
          conversions: googleResult?.conversions || 0,
          clicks: googleResult?.clicks || 0,
          impressions: googleResult?.impressions || 0
        };
      } catch (error) {
        console.error('Erro ao carregar dados do Google:', error);
        setGoogleError(true);
        newMetrics.google = {
          spend: 0,
          conversions: 0,
          clicks: 0,
          impressions: 0
        };
      }

      setMetrics(newMetrics);
    } catch (error) {
      console.error('Erro ao carregar métricas:', error);
      setError('Falha ao carregar dados. Por favor, tente novamente.');
    } finally {
      setMetricsLoading(false);
    }
  };

  useEffect(() => {
    const storedUser = localStorage.getItem('user')
    if (storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser)
        if (parsedUser.token) {
          setUser(parsedUser)
        }
      } catch (error) {
        console.error('Error parsing stored user:', error)
        localStorage.removeItem('user')
      }
    }
  }, [])

  useEffect(() => {
    if (user?.isAuthenticated && user?.token) {
      if (!isCustomDate || (dateRange.from && dateRange.to)) {
        fetchMetrics()
      }
    }
  }, [selectedPeriod, dateRange, isCustomDate, user?.isAuthenticated])

  const handleLogin = async (email: string) => {
    setLoading(true)
    setError('')

    try {
      const response = await fetch('https://backend.testeswaffle.org/auth/magic-link', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ email })
      })

      const data = await response.json()

      if (!response.ok) {
        throw new Error(data.message || 'Erro ao enviar link de acesso')
      }

      toast.success("Link de acesso enviado! Verifique seu email para acessar sua conta.")
      setEmail('')
    } catch (err: any) {
      console.error(err)
      toast.error(err.message || 'Erro ao enviar link de acesso. Por favor, tente novamente.')
      setError(err.message || 'Erro ao enviar link de acesso. Por favor, tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  const handleLogout = () => {
    setUser(null)
    localStorage.removeItem('user')
  }

  const totalMetrics: PlatformMetrics = Object.values(metrics).reduce(
    (acc: PlatformMetrics, platformMetrics: PlatformMetrics | undefined) => ({
      spend: acc.spend + (platformMetrics?.spend || 0),
      conversions: acc.conversions + (platformMetrics?.conversions || 0),
      clicks: acc.clicks + (platformMetrics?.clicks || 0),
      impressions: acc.impressions + (platformMetrics?.impressions || 0),
    }),
    { spend: 0, conversions: 0, clicks: 0, impressions: 0 }
  );

  const cac = totalMetrics.conversions > 0 ? totalMetrics.spend / totalMetrics.conversions : 0
  const ctr = totalMetrics.impressions > 0 ? (totalMetrics.clicks / totalMetrics.impressions) * 100 : 0

  const sortedPlatformData = Object.entries(metrics)
    .map(([platform, data]) => ({
      name: platform.charAt(0).toUpperCase() + platform.slice(1),
      conversions: data?.conversions || 0
    }))
    .sort((a, b) => (b.conversions || 0) - (a.conversions || 0))

  const tabs = [
    { value: "overview", label: "Visão Geral" },
    { value: "urlshortener", label: "URL Shortener" },
    { value: "thenews", label: "Cadernos" },
    { value: "upload", label: "Upload" },
    { value: "streaks", label: "Streaks" },
    { value: "changelogs", label: "Changelogs" }
  ]

  if (!user?.isAuthenticated) {
    if (pageLoading) {
      return (
            <LoadingSpinner />
      )
    }

    return (
      <LoginForm
        handleLogin={handleLogin}
        error={error}
        loading={loading}
        email={email}
        setEmail={setEmail}
      />
    )
  }

  return (
    <ThemeProvider defaultTheme="system" storageKey="ui-theme">
      <Router>
        <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
          <header className="sticky top-0 z-50 w-full border-b bg-white dark:bg-gray-800 dark:border-gray-700"> 
          
            <div className="container flex h-16 items-center justify-between px-4">
              <div className="flex items-center gap-4">
                <img
                  src="https://dash.testeswaffle.org/logo.png"
                  alt="Waffle Logo"
                  className="flex h-8 w-auto"
                />
              </div>
              <div className="hidden md:flex flex-1 max-w-md mx-8">
                <div className="relative w-full">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400 dark:text-gray-400" /> 
                  <Input placeholder="Buscar métricas..." className="pl-8 bg-white dark:bg-gray-800" />
                </div>
                
              </div>
              <div className="flex items-center gap-4">
              <div className="flex items-center gap-4">
                  <ThemeToggle />
                </div>
                <Button variant="ghost" size="icon" className="relative">
                  <Bell className="h-5 w-5 text-gray-900 dark:text-gray-100" />
                  <span className="absolute top-1 right-1 h-2 w-2 bg-[#FF3366] rounded-full" />
                </Button>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" className="relative h-8 w-8 rounded-full">
                      <Avatar className="h-8 w-8">
                        <AvatarImage src="/placeholder.svg" alt={user.email} />
                        <AvatarFallback>{user.email.charAt(0).toUpperCase()}</AvatarFallback>
                      </Avatar>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent className="w-56 bg-[#FFFFFF] dark:bg-gray-800" align="end" forceMount> 
                    <DropdownMenuLabel className="font-normal text-gray-900 dark:text-gray-100"> 
                      <div className="flex flex-col space-y-1">
                        <p className="text-sm font-medium leading-none">{user.email}</p>
                        <p className="text-xs leading-none text-muted-foreground dark:text-gray-400"> 
                          Administrador
                        </p>
                      </div>
                    </DropdownMenuLabel>
                    <DropdownMenuSeparator className="dark:border-gray-700" /> 
                    <DropdownMenuItem onSelect={() => setShowPasswordChange(true)}>
                      <Settings className="mr-2 h-4 w-4" />
                      <span>Alterar Senha</span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={handleLogout}>
                      <Lock className="mr-2 h-4 w-4" />
                      <span>Sair</span>
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            </div>
          </header>
          <Banner />

          <main className="flex-1">
            <div className="container max-w-6xl mx-auto">
              <Routes>
                <Route 
                  path="/" 
                  element={
                    <Tabs defaultValue="overview" className="w-full">
                      <div className="flex items-center justify-between">
                        <TabsList className="w-full flex overflow-x-auto md:justify-center h-12 bg-transparent border-b-0 p-0 scrollbar-none">
                          {tabs.map((tab) => (
                            <TabsTrigger
                              key={tab.value}
                              value={tab.value}
                              className="h-12 rounded-none border-b-2 border-transparent data-[state=active]:border-[#FF3366] data-[state=active]:bg-white dark:data-[state=active]:bg-gray-800 dark:data-[state=active]:border-[#FF3366] data-[state=active]:text-[#FF3366]"
                            >
                              {tab.label}
                            </TabsTrigger>
                          ))}
                        </TabsList>
                      </div>

                      {tabs.map((tab) => (
                        <TabsContent key={tab.value} value={tab.value} className="space-y-6 pt-12 mb-8">
                          {tab.value === "streaks" && <StreaksDashboard />}
                          {tab.value === "upload" && <UploadAnalysis />}
                          {tab.value === "urlshortener" && <URLShortener />}
                          {tab.value === "campaigns" && <CampaignsView />}
                          {tab.value === "thenews" && <NewsletterInsights initialCaderno="todos" />}
                          {tab.value === "changelogs" && <Changelog />}
                          {tab.value === "overview" && (
                            <>
                              <div className="w-full px-4 md:px-0">
                                <div className="flex justify-end mb-4">
                                  <DateRangePicker />
                                </div>
                              </div>

                              <div className="grid gap-6 w-full px-4 md:px-0 md:grid-cols-2">
                                {googleError && (
                                  <Alert variant="destructive" className="col-span-2 mb-4">
                                    <AlertDescription>
                                      Não foi possível carregar os dados do Google. Os totais exibidos consideram apenas Meta e TikTok.
                                    </AlertDescription>
                                  </Alert>
                                )}

                                <Card className="shadow-sm border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800"> {/* Alterado para modo escuro */}
                                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="text-sm font-medium text-gray-900 dark:text-gray-100">Total Gasto</CardTitle> {/* Alterado para modo escuro */}
                                    <BadgeDollarSign className="h-4 w-4 text-[#FF3366]" />
                                  </CardHeader>
                                  <CardContent>
                                    {metricsLoading ? (
                                      <MetricsLoadingSpinner />
                                    ) : (
                                      <>
                                        <div className="text-2xl font-bold text-gray-900 dark:text-gray-100"> {/* Alterado para modo escuro */}
                                          R$ {totalMetrics.spend.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </div>
                                        <p className="text-xs text-muted-foreground dark:text-gray-400 mt-2"> {/* Alterado para modo escuro */}
                                          Valor investido no período{googleError ? ' (exceto Google)' : ''}.
                                        </p>
                                      </>
                                    )}
                                  </CardContent>
                                </Card>

                                <Card className="shadow-sm border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800"> {/* Alterado para modo escuro */}
                                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="text-sm font-medium text-gray-900 dark:text-gray-100">Total de Conversões</CardTitle> {/* Alterado para modo escuro */}
                                    <Target className="h-4 w-4 text-[#FF3366]" />
                                  </CardHeader>
                                  <CardContent>
                                    {metricsLoading ? (
                                      <MetricsLoadingSpinner />
                                    ) : (
                                      <>
                                        <div className="text-2xl font-bold text-gray-900 dark:text-gray-100"> {/* Alterado para modo escuro */}
                                          {totalMetrics.conversions.toLocaleString()}
                                        </div>
                                        <p className="text-xs text-muted-foreground dark:text-gray-400 mt-2"> {/* Alterado para modo escuro */}
                                          Conversões únicas.
                                        </p>
                                      </>
                                    )}
                                  </CardContent>
                                </Card>

                                <Card className="shadow-sm border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800"> {/* Alterado para modo escuro */}
                                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="text-sm font-medium text-gray-900 dark:text-gray-100">Total de Cliques</CardTitle> {/* Alterado para modo escuro */}
                                    <MousePointerClick className="h-4 w-4 text-[#FF3366]" />
                                  </CardHeader>
                                  <CardContent>
                                    {metricsLoading ? (
                                      <MetricsLoadingSpinner />
                                    ) : (
                                      <>
                                        <div className="text-2xl font-bold text-gray-900 dark:text-gray-100"> {/* Alterado para modo escuro */}
                                          {totalMetrics.clicks.toLocaleString()}
                                        </div>
                                        <p className="text-xs text-muted-foreground dark:text-gray-400 mt-2"> {/* Alterado para modo escuro */}
                                          Cliques únicos.
                                        </p>
                                      </>
                                    )}
                                  </CardContent>
                                </Card>

                                <Card className="shadow-sm border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800"> {/* Alterado para modo escuro */}
                                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="text-sm font-medium text-gray-900 dark:text-gray-100">Total de Impressões</CardTitle> {/* Alterado para modo escuro */}
                                    <Eye className="h-4 w-4 text-[#FF3366]" />
                                  </CardHeader>
                                  <CardContent>
                                    {metricsLoading ? (
                                      <MetricsLoadingSpinner />
                                    ) : (
                                      <>
                                        <div className="text-2xl font-bold text-gray-900 dark:text-gray-100"> {/* Alterado para modo escuro */}
                                          {totalMetrics.impressions.toLocaleString()}
                                        </div>
                                        <p className="text-xs text-muted-foreground dark:text-gray-400 mt-2"> {/* Alterado para modo escuro */}
                                          Impressões únicas.
                                        </p>
                                      </>
                                    )}
                                  </CardContent>
                                </Card>

                                <Card className="shadow-sm border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800"> {/* Alterado para modo escuro */}
                                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="text-sm font-medium text-gray-900 dark:text-gray-100">Customer Acquisition Cost</CardTitle> {/* Alterado para modo escuro */}
                                    <UserRound className="h-4 w-4 text-[#FF3366]" />
                                  </CardHeader>
                                  <CardContent>
                                    {metricsLoading ? (
                                      <MetricsLoadingSpinner />
                                    ) : (
                                      <>
                                        <div className="text-2xl font-bold text-gray-900 dark:text-gray-100"> {/* Alterado para modo escuro */}
                                          R$ {cac.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                        </div>
                                        <p className="text-xs text-muted-foreground dark:text-gray-400 mt-2"> {/* Alterado para modo escuro */}
                                          Custo / Conversões = CAC
                                        </p>
                                      </>
                                    )}
                                  </CardContent>
                                </Card>

                                <Card className="shadow-sm border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800"> {/* Alterado para modo escuro */}
                                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                                    <CardTitle className="text-sm font-medium text-gray-900 dark:text-gray-100">Click-through Rate</CardTitle> {/* Alterado para modo escuro */}
                                    <Percent className="h-4 w-4 text-[#FF3366]" />
                                  </CardHeader>
                                  <CardContent>
                                    {metricsLoading ? (
                                      <MetricsLoadingSpinner />
                                    ) : (
                                      <>
                                        <div className="text-2xl font-bold text-gray-900 dark:text-gray-100"> {/* Alterado para modo escuro */}
                                          {ctr.toFixed(2)}%
                                        </div>
                                        <p className="text-xs text-muted-foreground dark:text-gray-400 mt-2"> {/* Alterado para modo escuro */}
                                          Clicks / Impressions * 100
                                        </p>
                                      </>
                                    )}
                                  </CardContent>
                                </Card>
                              </div>

                              <div className="grid gap-6 w-full px-4 md:px-0 md:grid-cols-2">
                                <Card className="w-full overflow-hidden bg-white dark:bg-gray-800"> {/* Alterado para modo escuro */}
                                  <CardHeader>
                                    <CardTitle className="text-gray-900 dark:text-gray-100">Conversões por Plataforma</CardTitle> {/* Alterado para modo escuro */}
                                  </CardHeader>
                                  <CardContent>
                                    <ChartContainer
                                      config={{
                                        conversions: {
                                          label: "Conversões: ",
                                          color: "#FF3366",
                                        },
                                      }}
                                      className="w-full px-4 md:px-0 md:grid-cols-2"
                                    >
                                      <ResponsiveContainer width="100%" height='100%'>
                                        <BarChart data={sortedPlatformData}
                                          margin={{
                                            top: 20,
                                            right: 0, // Margem reduzida para mobile
                                            left: 0,  // Margem reduzida para mobile
                                            bottom: 20,
                                          }}
                                          layout="vertical">
                                          <CartesianGrid strokeDasharray="3 3" stroke="#gray-700" /> {/* Alterado para melhor visibilidade no modo escuro */}
                                          <XAxis type="number" stroke="#gray-400" /> {/* Alterado para melhor visibilidade no modo escuro */}
                                          <YAxis dataKey="name" type="category" stroke="#gray-400" /> {/* Alterado para melhor visibilidade no modo escuro */}
                                          <ChartTooltip content={<ChartTooltipContent className='bg-gray-800 dark:bg-gray-800' />} /> {/* Alterado para modo escuro */}
                                          <Bar dataKey="conversions" fill="#FF3366" /> {/* Cor ajustada para melhor visibilidade */}
                                        </BarChart>
                                      </ResponsiveContainer>
                                    </ChartContainer>
                                  </CardContent>
                                </Card>

                                <Card className="shadow-sm border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800"> {/* Alterado para modo escuro */}
                                  <CardHeader>
                                    <CardTitle className="text-gray-900 dark:text-gray-100">Desempenho por Plataforma (Dados)</CardTitle> {/* Alterado para modo escuro */}
                                  </CardHeader>
                                  <CardContent>
                                    <ScrollArea className="h-[300px] md:h-[400px] lg:h-[300px]"> {/* Ajusta altura para diferentes telas */}
                                      <div className="space-y-4">
                                        {Object.entries(metrics).map(([platform, data]) => (
                                          <div key={platform} className="flex flex-col md:flex-row items-center justify-between p-4 border rounded-lg border-gray-200 dark:border-gray-700"> {/* Flex-col para mobile, flex-row para desktop */}
                                            <div className="w-full md:w-1/2"> {/* Ajusta largura para desktop */}
                                              <h3 className="font-medium text-lg md:text-xl text-gray-900 dark:text-gray-100"> {/* Ajusta tamanho do texto e cor para modo escuro */}
                                                {platform.charAt(0).toUpperCase() + platform.slice(1)}
                                              </h3>
                                            </div>
                                            <div className="w-full md:w-1/2 text-right mt-4 md:mt-0"> {/* Ajusta margem para mobile */}
                                              <p className="font-medium text-lg md:text-xl text-gray-900 dark:text-gray-100"> {/* Ajusta tamanho do texto e cor para modo escuro */}
                                                R$ {(data?.spend || 0).toLocaleString('pt-BR', {
                                                  minimumFractionDigits: 2,
                                                  maximumFractionDigits: 2
                                                })}
                                              </p>
                                              <p className="text-sm text-muted-foreground dark:text-gray-400">
                                                {(data?.conversions || 0).toLocaleString()} conversões
                                              </p>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </ScrollArea>
                                  </CardContent>
                                </Card>
                               
                              </div>
                              <div className="grid gap-6 md:grid-cols-3 px-4 md:px-0">
                                {Object.entries(metrics).map(([platform, data]) => (
                                  <Card key={platform} className="shadow-sm border border-gray-200 dark:border-gray-700 rounded-md bg-white dark:bg-gray-800">
                                    <CardHeader>
                                      <CardTitle className="text-gray-900 dark:text-gray-100">{platform.charAt(0).toUpperCase() + platform.slice(1)}</CardTitle>
                                    </CardHeader>
                                    <CardContent>
                                      <div className="space-y-2">
                                        <div className="flex justify-between">
                                          <span>Gasto:</span>
                                          <span>R$ {(data?.spend || 0).toLocaleString('pt-BR', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                          })}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span>Conversões:</span>
                                          <span>{(data?.conversions || 0).toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span>Cliques:</span>
                                          <span>{(data?.clicks || 0).toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span>Impressões:</span>
                                          <span>{(data?.impressions || 0).toLocaleString()}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span>Customer Acquisition Cost:</span>
                                          <span>R$ {(data?.conversions > 0 ? data?.spend / data?.conversions : 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                                        </div>
                                        <div className="flex justify-between">
                                          <span>Click-through Rate:</span>
                                          <span>{(data?.impressions > 0 ? (data?.clicks / data?.impressions) * 100 : 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}%</span>
                                        </div>
                                      </div>
                                    </CardContent>
                                  </Card>
                                ))}
                              </div>
                              <DailyMetricsChart />
                              <Newsletters />
                            </>
                          )}
                        </TabsContent>
                      ))}
                    </Tabs>
                  } 
                />
                <Route path="/hiring" element={<FullStackCandidatesDashboard />} />
                <Route path="/thenews" element={<NewsletterInsights initialCaderno="todos" />} />
                <Route path="/upload" element={<UploadAnalysis />} />
                <Route path="/streaks" element={<StreaksDashboard />} />
                <Route path="/changelogs" element={<Changelog />} />
               
              </Routes>
            </div>
          </main>

          {showPasswordChange && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg"> {/* Alterado para modo escuro */}
                <PasswordChange />
                <Button onClick={() => setShowPasswordChange(false)} className="mt-4">Fechar</Button>
              </div>
            </div>
          )}
          <ChatBubble />
          <Cookie />
          <Footer />
        </div>
      </Router>
    </ThemeProvider>
  );
}